import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IErrorDescription } from '@ibank-globals/error';
import { LanguageService } from '@ibank-globals/language';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators/finalize';
import { BaseComponent } from '../../../globals/classes/base.component';
import { SourceApis } from '../../../globals/classes/errorDescription';
import { ErrorHandler } from '../../../globals/classes/errorHandler';
import { RSAKey } from '../../../globals/classes/rsa/rsaKey';
import { AppSettings } from '../../app.settings';
import { ISendIbankTermsRequest } from '../../classes/existing-onboarding-customer/requests';
import { CreateApplicationResponse } from '../../classes/existing-onboarding-customer/responses';
import { ISignInRequest } from '../../classes/http-requests';
import {
	ITwoFactorAuthenticationInfo,
	TwoFactorAuthenticationInfo,
} from '../../classes/http-responses';
import { IInitializePasswordRequest } from '../../classes/initialize-password';
import { Extensions } from '../../classes/static/extensions';
import { GoogleAnalyticsUtils } from '../../classes/static/google-analytics-utils';
import { UserAuth } from '../../classes/userAuth';
import { ExistingOnboardingCustomerService } from '../../services/existing-recovery/customer.service';
import { TwoFAModalComponent } from '../two-fa-modal/two-fa-modal.component';

@Component({
	selector: 'complete-password',
	template: require('./complete-password.component.html'),
})
export class CompletePasswordComponent extends BaseComponent implements OnInit {
	@Input() initRequest: IInitializePasswordRequest;
	@Input() ibUsernameRequest: string;
	@Input() appCreateAppResponse: CreateApplicationResponse;
	@Input() isOnboardingFlow: boolean;
	public isRebrading = AppSettings.isRebrading;

	public sendIbankTermsRequest: ISendIbankTermsRequest;
	public signInRequest: ISignInRequest;
	public isLoading: boolean = false;
	public langId: string;
	public dynatraceForExistingOnBoarding: boolean = AppSettings.dynatraceForExistingOnBoarding;

	constructor(
		private _langSvc: LanguageService,
		private activeModal: NgbActiveModal,
		private modalService: NgbModal,
		private route: ActivatedRoute
	) {
		super();
	}

	public ngOnInit(): void {
		this.initRequests();
		this._initSubscriptions();
		this.invokeSendIbankTerms();

		this.route.queryParams.subscribe((params) => {
			this.signInRequest.returnUrl = Extensions.getReturnUrl(params)
				? Extensions.getReturnUrl(params)
				: Extensions.returnUrl;
			Extensions.returnUrl = this.signInRequest.returnUrl;
		});
	}

	public connectNow(): void {
		if (this.dynatraceForExistingOnBoarding) this._setConnectDynatraceEvent();
		this.activeModal.close();
		this.performLogin();
	}

	private _setConnectDynatraceEvent() {
		this.setDynatraceEvent('ECO_Connect_Internet_Banking');
	}

	private _initSubscriptions(): void {
		this.mortalize(this._langSvc.activeLanguage$).subscribe((lang) => (this.langId = lang));
	}

	private initRequests(): void {
		this.sendIbankTermsRequest = {
			appId: this.appCreateAppResponse.appInfo.appId,
			lang: '',
		};
		this.signInRequest = {
			username: undefined,
			password: undefined,
			rememberLogin: undefined,
			returnUrl: undefined,
			userID: undefined,
		};
	}

	private invokeSendIbankTerms(): void {
		if (!this.ibUsernameRequest) return;
		this.isLoading = true;
		const sendIbankTermsReq = {
			appId: this.appCreateAppResponse.appInfo.appId,
			lang: this.langId,
		};

		ExistingOnboardingCustomerService.sendIbankTerms(sendIbankTermsReq)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => {},
				(error) => this.handleError(error, SourceApis.Onboarding)
			);
	}

	private handleSendIbankTermsResponse(response: boolean) {
		if (!!response && response == true) {
			console.log('The Ibank Terms are sent!');
		}
	}
	private handleError(error: IErrorDescription, sourceApi: SourceApis): void {
		ErrorHandler.newError(error, sourceApi);
	}

	private performLogin() {
		if (!this.initRequest.passwordNew) return;
		this.isLoading = true;
		this.signInRequest.username = this.ibUsernameRequest;
		this.signInRequest.rememberLogin = false;

		if (AppSettings.enforcePasswordEncryption) {
			let rsaKey = new RSAKey();
			rsaKey.setPublic(AppSettings.getPropertyN(), AppSettings.getPropertyE());
			this.signInRequest.password = rsaKey.encrypt(this.initRequest.passwordNew);
		} else {
			this.signInRequest.password = this.initRequest.passwordNew;
		}

		GoogleAnalyticsUtils.sendLoginEvent();

		UserAuth.signIn(this.signInRequest)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleSignInResponse(result),
				(error) => {
					this.handleError(error, SourceApis.Identity);
				}
			);
	}

	private handleSignInResponse(response: ITwoFactorAuthenticationInfo) {
		if (!response) return;
		this.goToCodeVerification(response);
	}

	private goToCodeVerification(twofaInfo: ITwoFactorAuthenticationInfo): void {
		const modalRef = this.modalService.open(TwoFAModalComponent, {
			windowClass: 'modal-small',
		});
		(modalRef.componentInstance as TwoFAModalComponent).twoFAInfo =
			new TwoFactorAuthenticationInfo(twofaInfo);
	}
}
